import React from 'react';
import { CourseDesign } from '../../../services/designRunsService';
import { Card, LinesEditor, TextArea } from './editors';

/** Course-level design output: arc, goals, outcomes, glossary. */
export function OverviewTab({ design, editing, onChange }: {
  design: CourseDesign;
  editing: boolean;
  onChange: (next: CourseDesign) => void;
}) {
  const syllabus = design.syllabus;
  if (!syllabus) return <div className="text-sm text-gray-400">Not designed yet.</div>;
  const setSyllabus = (patch: Partial<NonNullable<CourseDesign['syllabus']>>) =>
    onChange({ ...design, syllabus: { ...syllabus, ...patch } });

  return (
    <div className="space-y-3">
      <Card>
        <div className="text-base font-medium text-gray-900 mb-1">How the course flows</div>
        {editing ? (
          <TextArea rows={3} value={syllabus.narrative_arc ?? ''}
            onChange={(narrative_arc) => setSyllabus({ narrative_arc })} />
        ) : (
          <p className="text-base text-gray-700">{syllabus.narrative_arc || '—'}</p>
        )}
      </Card>
      <Card>
        <div className="text-base font-medium text-gray-900 mb-1">What you'll learn</div>
        {editing ? (
          <LinesEditor values={design.scope.learning_objectives}
            onChange={(learning_objectives) =>
              onChange({ ...design, scope: { ...design.scope, learning_objectives } })} />
        ) : (
          <ul className="text-base text-gray-700 list-disc ml-4">
            {design.scope.learning_objectives.map((o) => <li key={o}>{o}</li>)}
          </ul>
        )}
      </Card>
      {(editing || (design.scope.vocab_exceptions ?? []).length > 0) && (
        <Card>
          <div className="text-base font-medium text-gray-900 mb-1">Vocabulary exceptions</div>
          <div className="text-xs text-gray-500 mb-2">
            Words the level gate must allow for this course — the subject matter may need
            vocabulary above the target level (one per line).
          </div>
          {editing ? (
            <LinesEditor values={design.scope.vocab_exceptions ?? []}
              onChange={(vocab_exceptions) =>
                onChange({ ...design, scope: { ...design.scope, vocab_exceptions } })} />
          ) : (
            <div className="text-sm text-gray-700">
              {(design.scope.vocab_exceptions ?? []).join(' · ')}
            </div>
          )}
        </Card>
      )}
      <Card>
        <div className="text-base font-medium text-gray-900 mb-1">
          Goals — by the end of this course you will be able to…
        </div>
        {editing ? (
          <LinesEditor values={syllabus.structured_goals ?? []}
            onChange={(structured_goals) => setSyllabus({ structured_goals })} />
        ) : (
          <ul className="text-base text-gray-700 list-disc ml-4">
            {(syllabus.structured_goals ?? []).map((g) => <li key={g}>{g}</li>)}
          </ul>
        )}
      </Card>
      {(syllabus.course_glossary ?? []).length > 0 && (
        <Card>
          <div className="text-base font-medium text-gray-900 mb-1">Glossary</div>
          <dl className="text-base text-gray-700 space-y-1">
            {syllabus.course_glossary!.map((g) => (
              <div key={g.term}>
                <dt className="inline font-medium">{g.term}</dt>
                <dd className="inline text-gray-500"> — {g.definition}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}
    </div>
  );
}
